export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, details: unknown) {
    super(code);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type ApiPolicy = "private" | "public";

async function requestJson<T>(path: string, init: RequestInit = {}, policy: ApiPolicy): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body != null && !headers.has("content-type") && typeof init.body === "string") {
    headers.set("content-type", "application/json");
  }
  if (policy === "private") headers.set("cache-control", "no-store");

  const response = await fetch(path, {
    ...init,
    headers,
    cache: policy === "private" ? "no-store" : init.cache,
  });
  const contentType = response.headers.get("content-type") ?? "";
  const value: unknown = contentType.includes("application/json")
    ? await response.json().catch(() => ({}))
    : await response.text().catch(() => "");
  if (!response.ok) {
    const code = value && typeof value === "object" && "error" in value && typeof value.error === "string"
      ? value.error
      : `request_failed_${response.status}`;
    throw new ApiError(response.status, code, value);
  }
  return value as T;
}

export function api<T>(path: string, init?: RequestInit): Promise<T> {
  return requestJson<T>(path, init, "private");
}

export function publicApi<T>(path: string, init?: RequestInit): Promise<T> {
  return requestJson<T>(path, init, "public");
}

const retryKeys = new Map<string, string>();

function requiresMutationIdempotency(path: string): boolean {
  return path === "/api/captures/analyze" || /^\/api\/moves\/[^/]+\/check-in$/.test(path);
}

async function idempotentPost<T>(path: string, body: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  const signature = `${path}\u001f${body}`;
  if (!headers.has("idempotency-key")) {
    const existing = retryKeys.get(signature);
    const key = existing ?? crypto.randomUUID();
    retryKeys.set(signature, key);
    headers.set("idempotency-key", key);
  }
  try {
    const result = await api<T>(path, { ...init, headers, method: "POST", body });
    retryKeys.delete(signature);
    return result;
  } catch (error) {
    throw error;
  }
}

export const get = <T,>(path: string, init?: RequestInit) => api<T>(path, { ...init, method: "GET" });
export const post = <T,>(path: string, data: unknown, init?: RequestInit) => {
  const body = JSON.stringify(data);
  return requiresMutationIdempotency(path)
    ? idempotentPost<T>(path, body, init)
    : api<T>(path, { ...init, method: "POST", body });
};
export const patch = <T,>(path: string, data: unknown, init?: RequestInit) => api<T>(path, { ...init, method: "PATCH", body: JSON.stringify(data) });
export const remove = <T,>(path: string, init?: RequestInit) => api<T>(path, { ...init, method: "DELETE" });
export const publicPost = <T,>(path: string, data: unknown, init?: RequestInit) => publicApi<T>(path, { ...init, method: "POST", body: JSON.stringify(data) });
